import React from 'react';
import Icon from '../../../components/AppIcon';


const FileQueueItem = ({ file, onRemove, onRetry, onCancel }) => {
  const getStatusColor = () => {
    switch (file?.status) {
      case 'completed': return 'text-green-600 dark:text-green-400';
      case 'uploading': return 'text-blue-600 dark:text-blue-400';
      case 'error': return 'text-red-600 dark:text-red-400';
      case 'canceled': return 'text-yellow-600 dark:text-yellow-400';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = () => {
    switch (file?.status) {
      case 'completed': return 'CheckCircle2';
      case 'uploading': return 'Loader2';
      case 'error': return 'AlertCircle';
      case 'canceled': return 'XCircle';
      default: return 'Clock';
    }
  };

  const getStatusText = () => {
    switch (file?.status) {
      case 'completed': return 'Completed';
      case 'uploading': return `Uploading... ${file?.progress || 0}%`;
      case 'error': 
        if (file?.isTimeout) {
          return `Timeout - Upload exceeded time limit`;
        }
        return 'Failed';
      case 'canceled': return 'Canceled';
      default: return 'Pending';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(2)} MB`;
  };

  const formatElapsedTime = (ms) => {
    if (!ms) return '';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const showRetryButton = file?.status === 'error' && !file?.corsError;
  const showCancelButton = file?.status === 'uploading' && onCancel;

  return (
    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
      <div className="flex items-start gap-3">
        <Icon 
          name={getStatusIcon()} 
          size={20} 
          className={`mt-1 ${getStatusColor()} ${file?.status === 'uploading' ? 'animate-spin' : ''}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground truncate" title={file?.name}>
                {file?.name}
              </p>
              <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                <span>{formatFileSize(file?.size)}</span>
                <span className={getStatusColor()}>
                  {getStatusText()}
                </span>
                {file?.status === 'uploading' && file?.speed && (
                  <span className="text-blue-600 dark:text-blue-400">
                    {file?.speed}
                  </span>
                )}
                {file?.status === 'uploading' && file?.elapsed && (
                  <span className="text-muted-foreground">
                    {formatElapsedTime(file?.elapsed)}
                  </span>
                )}
                {file?.retryCount > 0 && (
                  <span className="text-yellow-600 dark:text-yellow-400">
                    Retry {file?.retryCount}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {showCancelButton && (
                <button
                  onClick={onCancel}
                  className="focus-ring rounded p-1.5 hover:bg-yellow-100 dark:hover:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 transition-colors"
                  title="Cancel upload"
                >
                  <Icon name="X" size={16} />
                </button>
              )}
              {showRetryButton && (
                <button
                  onClick={() => onRetry(file?.id)}
                  className="focus-ring rounded p-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400 transition-colors"
                  title="Retry upload"
                >
                  <Icon name="RotateCw" size={16} />
                </button>
              )}
              {file?.status !== 'uploading' && (
                <button
                  onClick={() => onRemove(file?.id)}
                  className="focus-ring rounded p-1.5 hover:bg-destructive/10 text-destructive transition-colors"
                  title="Remove from queue"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              )}
            </div>
          </div>

          {file?.status === 'uploading' && (
            <div className="mt-3">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 dark:bg-blue-500 transition-all duration-300 ease-out"
                  style={{ width: `${file?.progress || 0}%` }}
                />
              </div>
            </div>
          )}

          {file?.error && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-900 dark:text-red-100 font-medium mb-1">
                {file?.isTimeout ? '⏱️ Upload Timeout' : '❌ Upload Failed'}
              </p>
              <p className="text-xs text-red-700 dark:text-red-300">
                {file?.error}
              </p>
              {file?.isTimeout && (
                <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                  <p className="font-medium">💡 Troubleshooting Steps:</p>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li>Check your internet connection speed</li>
                    <li>Verify S3 bucket CORS configuration</li>
                    <li>Try uploading a smaller file first</li>
                    <li>Check browser console (F12) for detailed logs</li>
                  </ul>
                </div>
              )}
              {file?.corsError && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                  See validation alert above for CORS configuration instructions.
                </p>
              )}
            </div>
          )}

          {file?.status === 'canceled' && (
            <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <p className="text-sm text-yellow-900 dark:text-yellow-100">
                ⚠️ Upload was canceled
              </p>
            </div>
          )}

          {file?.status === 'completed' && file?.fileUrl && (
            <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
              <p className="text-sm text-green-900 dark:text-green-100 font-medium">
                ✅ Upload successful
              </p>
              <a 
                href={file?.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-green-700 dark:text-green-300 hover:underline mt-1 inline-flex items-center gap-1"
              >
                View file
                <Icon name="ExternalLink" size={12} />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileQueueItem;